import React from "react";
import { useTranslation } from "react-i18next";
import { CheckboxIcon, StyledH2, StyledLabel } from "@aureskonnect/react-ui";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";
import { mutate } from "swr";
import { toast } from "react-toastify";
import { diff } from "deep-diff";
import moment from "moment";

import {
    store,
    setIsModalConfirmationConfigurationKiosk,
    setIsLoading,
} from "../../../store/project/index";
import { generalConfigStore } from "@store";
import { store as userStore } from "@components/VerticalLayout/store";
import kioskSettingStore from "@pages/GeneralConfig/store";
import { setKey, store as localStore } from "../store";

import { capitalize } from "../../../helpers/general";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { getDiff } from "./hooks/ConfigurationKiosk";
import { useFetchGlobal } from "../../../api/hooks";

type ModalConfirmationConfigurationKioskType = {
    setIsModalLoadingOpened: Function;
    setModalLoadingData: Function;
};

export default function ModalConfirmationConfigurationKiosk({
    setIsModalLoadingOpened,
    setModalLoadingData,
}: ModalConfirmationConfigurationKioskType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { isModalConfirmationConfigurationKiosk, kioskProject } = useSnapshot(
        store
    );

    const { itemsDisable } = useSnapshot(generalConfigStore);

    const {
        userID,
        projectId,
        franchiseID,
        oneShop,
        operatorID,
        shopID,
        fideltyType,
    } = useSnapshot(userStore);
    const { projectRowData } = useSnapshot(kioskSettingStore);
    const { initialProject, difference } = useSnapshot(localStore);

    const uuidUser = localStorage.getItem("uuidUser");

    const fetchData = async (apiUrl: string) => {
        try {
            const data = await fetchGlobal<any>(apiUrl, {
                headers: {
                    "Content-Type": "application/json",
                },
                method: "GET",
            });
            return JSON.parse(JSON.stringify(data[0]));
        } catch (error) {
            console.error("Error fetching data:", error);
            throw error;
        }
    };

    const processDifferences = (initial: any, modified: any) => {
        let differences: any = diff(initial, modified);
        if (differences) {
            Object.keys(modified.template.content)
                .filter(
                    (item: any) =>
                        item !== "TPAValina" &&
                        item !== "generalDesign" &&
                        item !== "finalMessage"
                )
                .forEach((el: any) => {
                    const diff = getDiff(el, kioskProject, initialProject);
                    differences = differences.filter(
                        (elt: any) =>
                            elt.path[2] !== el || elt.path.includes("id")
                    );
                    differences = [...differences, ...diff];
                });
        }
        return differences;
    };

    const processUpdatedData = async (
        modifiedDataProject: any,
        differences: any,
        shopId: string,
        warn: boolean
    ) => {
        let changedItems: any = [];
        modifiedDataProject = await updateObjectBasedOnDifferences(
            differences,
            modifiedDataProject,
            kioskProject
        );
        modifiedDataProject = await updateFilesBasedOnDifferences(
            differences,
            modifiedDataProject,
            kioskProject
        );
        differences
            .filter(
                (diff: any) =>
                    !diff.path.includes("standbyScreen") &&
                    !diff.path.includes("standbyStateScreen") &&
                    !diff.path.includes("files")
            )
            .forEach((change: any) => {
                const { path } = change;
                const kind = change.item?.kind ?? change.kind;
                const rhs = change?.rhs ?? change?.item?.rhs ?? null;
                const deletedElement = change.item?.lhs ?? change.lhs ?? null;

                let data = applyChange(
                    modifiedDataProject,
                    path,
                    kind,
                    JSON.parse(JSON.stringify(rhs)),
                    warn,
                    changedItems,
                    JSON.parse(JSON.stringify(deletedElement)),
                    shopId
                );

                modifiedDataProject = data.obj;
                warn = data.warn;
            });
        return { modifiedDataProject, warn };
    };

    const processTemplateData = (
        dataProject: any,
        modifiedDataProject: any,
        warn: boolean
    ) => {
        let languageItems: any = [];
        if (
            modifiedDataProject.template.content.languages.items.every(
                (el: any) => el.active === false
            )
        ) {
            warn = true;
            languageItems = dataProject.template.content.languages.items;
        } else {
            languageItems =
                modifiedDataProject.template.content.languages.items;
        }
        let saleItems: any = modifiedDataProject.template.content.salesMethods.items.filter(
            (el: any) =>
                el.active === true &&
                el.id !== "f25e7c96-b5d3-4f2e-acd0-1500258283c2"
        );

        if (saleItems.length === 0) {
            warn = true;
            saleItems = dataProject.template.content.salesMethods.items;
        } else {
            saleItems = modifiedDataProject.template.content.salesMethods.items;
        }
        let paymentItems: any = [];
        if (
            modifiedDataProject.template.content.meansOfPayment.items.every(
                (el: any) => el.active === false
            )
        ) {
            warn = true;
            paymentItems = dataProject.template.content.meansOfPayment.items;
        } else {
            paymentItems =
                modifiedDataProject.template.content.meansOfPayment.items;
        }
        let template = {
            ...modifiedDataProject.template,
            content: {
                ...modifiedDataProject.template.content,
                languages: {
                    ...JSON.parse(
                        JSON.stringify(
                            modifiedDataProject.template.content.languages
                        )
                    ),
                    // eslint-disable-next-line no-loop-func
                    items: languageItems,
                },
                salesMethods: {
                    // eslint-disable-next-line no-loop-func
                    items: saleItems,
                },
                meansOfPayment: {
                    // eslint-disable-next-line no-loop-func
                    items: paymentItems,
                },
            },
        };

        return { template, files: modifiedDataProject.files, warn };
    };

    const getData = async (
        shopId: string,
        appId?: any,
        isApplication: boolean = false
    ) => {
        const apiUrl = isApplication
            ? `${process.env.REACT_APP_API_V2_URL}/settings/applicationTemplate?userId=106&shopId=${shopId}&franchiseId=${franchiseID}&projectId=${projectId}&applicationId=${appId}`
            : `${process.env.REACT_APP_API_V2_URL}/settings/projectData?userId=106&shopId=${shopId}&franchiseId=${franchiseID}&projectId=${projectId}`;

        try {
            let dataProject = await fetchData(apiUrl);
            let modifiedDataProject = JSON.parse(JSON.stringify(dataProject));

            const differences = isApplication
                ? processDifferences(
                      {
                          template: initialProject.template,
                          files: initialProject.files,
                      },
                      {
                          template: kioskProject.template,
                          files: kioskProject.files,
                      }
                  )
                : processDifferences(initialProject, kioskProject);

            let {
                modifiedDataProject: updatedData,
                warn,
            } = await processUpdatedData(
                modifiedDataProject,
                differences,
                shopId,
                false
            );

            const { template, files } = processTemplateData(
                dataProject,
                updatedData,
                warn
            );
            return { shopProject: { template, files }, warn };
        } catch (error) {
            console.error(
                `Error in getData (${
                    isApplication ? "Application" : "Project"
                }):`,
                error
            );
            return { shopProject: null, warn: false }; // Return default values in case of an error
        }
    };

    function applyChange(
        obj: any,
        path: any,
        kind: string,
        value: any,
        warn: boolean,
        changedItems: any,
        deletedElement: any,
        shopId: any
    ) {
        let indexOfItems: number = path.findIndex((el: any) => el === "items");
        let itemOccurrence =
            indexOfItems !== -1
                ? path.filter((el: any) => el === "items").length
                : 0;
        if (indexOfItems !== -1) {
            if (path[1] === "content") {
                let indexOfId: number = path.findIndex(
                    (el: any) => el === "id"
                );
                let newP = [];
                for (let index = 0; index <= indexOfItems; index++) {
                    newP.push(path[index]);
                }

                if (
                    (indexOfId !== -1 &&
                        !changedItems.includes(path[indexOfItems - 1])) ||
                    itemOccurrence === 2
                ) {
                    changedItems.push(path[indexOfItems - 1]);
                    let bracketNotationString = newP
                        .map((element: any) => {
                            if (!isNaN(element)) {
                                return `[${element}]`;
                            }
                            return `['${element}']`;
                        })
                        .join("");

                    let items: any = [];
                    let itemsShop: any = [];
                    let newItems: any = [];
                    // eslint-disable-next-line no-eval
                    eval(`items=kioskProject${bracketNotationString}`);
                    // eslint-disable-next-line no-eval
                    eval(`itemsShop=obj${bracketNotationString}`);

                    if (!newP.includes("languages")) {
                        items.forEach((el: any) => {
                            let index: number = itemsShop.findIndex(
                                (elt: any) => el.id === elt.id
                            );
                            if (index !== -1) {
                                newItems.push({
                                    ...itemsShop[index],
                                });
                            }
                        });
                        // eslint-disable-next-line no-eval
                        eval(`obj${bracketNotationString} = newItems`);
                    } else {
                        let favoredFranchise: any = items.find(
                            (el: any) => el.isDefault === true
                        ).id;
                        let isFavoredExistInShop: any = itemsShop.find(
                            (el: any) => el.id === favoredFranchise
                        );
                        if (
                            isFavoredExistInShop !== undefined &&
                            itemsDisable.find(
                                (disabled: any) =>
                                    disabled.id === favoredFranchise &&
                                    disabled.shopId === shopId
                            ) === undefined
                        ) {
                            items.forEach((el: any) => {
                                let index: number = itemsShop.findIndex(
                                    (elt: any) => el.id === elt.id
                                );
                                if (index !== -1) {
                                    let Shop = itemsShop[index];
                                    const isItemEnabled = (
                                        item: any,
                                        shopId: number
                                    ) => {
                                        const disabledItem = itemsDisable.find(
                                            (disabledItem: any) =>
                                                disabledItem.id === item.id &&
                                                disabledItem.shopId === shopId
                                        );
                                        return disabledItem;
                                    };

                                    let active =
                                        el.isDefault === true &&
                                        Shop.active === false &&
                                        isItemEnabled(el, shopId) === undefined
                                            ? true
                                            : Shop.active;

                                    newItems.push({
                                        ...itemsShop[index],
                                        isDefault: el.isDefault,
                                        active: active,
                                    });
                                }
                            });
                            // eslint-disable-next-line no-eval
                            eval(`obj${bracketNotationString} = newItems`);
                        } else {
                            warn = true;
                        }
                    }
                } else {
                    let x = [...newP, path[indexOfItems + 1]].reduce(
                        (obj1: any, key: any) => obj1[key],
                        kioskProject
                    );
                    let id = "";
                    if (x !== undefined) {
                        id = x.id;
                    }
                    let indexInShop: any = [...newP]
                        .reduce((obj1: any, key: any) => obj1[key], obj)
                        .findIndex((el: any) => el.id === id);

                    if (
                        itemsDisable.some(
                            (el: any) => el.id === id && el.shopId === shopId
                        ) &&
                        value === true
                    ) {
                        warn = true;
                    } else if (indexInShop !== -1) {
                        path.splice(indexOfItems + 1, 1, indexInShop);
                        // eslint-disable-next-line
                        let bracketNotationString = path
                            .map((element: any) => {
                                if (!isNaN(element)) {
                                    return `[${element}]`;
                                }
                                return `['${element}']`;
                            })
                            .join("");

                        let isFavored =
                            obj?.template?.content?.languages?.items[
                                indexInShop
                            ]?.isDefault ?? false;

                        if (
                            !(
                                path[2] === "languages" &&
                                !path.includes("isDefault") &&
                                value === false &&
                                isFavored === true
                            )
                        ) {
                            kind === "D"
                                ? // eslint-disable-next-line no-eval
                                  eval(`delete obj${bracketNotationString}`)
                                : // eslint-disable-next-line no-eval
                                  eval(`obj${bracketNotationString} = value`);
                        } else if (
                            path[2] === "languages" &&
                            value === false &&
                            isFavored === true
                        ) {
                            warn = true;
                        }
                    }
                }
            }
            if (
                path.includes("ways") ||
                path.includes("elements") ||
                path.includes("subSteps")
            ) {
                let keys = Object.keys(obj.template.pages[path[2]]);
                let bracketNotationString = path
                    .map((element: any) => {
                        if (!isNaN(element)) {
                            return `[${element}]`;
                        }
                        return `['${element}']`;
                    })
                    .join("");
                if (keys.includes(path[3])) {
                    kind === "D"
                        ? // eslint-disable-next-line no-eval
                          eval(`delete obj${bracketNotationString}`)
                        : // eslint-disable-next-line no-eval
                          eval(`obj${bracketNotationString} = value`);
                } else if (kind === "N") {
                    // eslint-disable-next-line no-eval
                    eval(`obj${bracketNotationString} = value`);
                }
            }
        } else {
            if (
                path.includes("ways") ||
                path.includes("elements") ||
                path.includes("subSteps")
            ) {
                let keys = Object.keys(obj.template.pages[path[2]]);
                let bracketNotationString = path
                    .map((element: any) => {
                        if (!isNaN(element)) {
                            return `[${element}]`;
                        }
                        return `['${element}']`;
                    })
                    .join("");

                if (keys.includes(path[3])) {
                    kind === "D"
                        ? // eslint-disable-next-line no-eval
                          eval(`delete obj${bracketNotationString}`)
                        : // eslint-disable-next-line no-eval
                          eval(`obj${bracketNotationString} = value`);
                } else if (kind === "N") {
                    // eslint-disable-next-line no-eval
                    eval(`obj${bracketNotationString} = value`);
                }
            } else {
                let pathSetting = path
                    .map((element: any) => {
                        if (!isNaN(element)) {
                            return `[${element}]`;
                        }
                        return `['${element}']`;
                    })
                    .join("");
                // eslint-disable-next-line no-eval
                eval(`obj${pathSetting} =
                            kioskProject${pathSetting}`);
            }
        }

        return { obj, warn };
    }
    async function updateObjectBasedOnDifferences(
        differences: any,
        obj: any,
        kioskProject: any
    ) {
        const hasStandbyScreen = differences.some((path: any) =>
            path.path.includes("standbyScreen")
        );

        const hasStandbyStateScreen = differences.some((path: any) =>
            path.path.includes("standbyStateScreen")
        );

        if (hasStandbyScreen) {
            obj.template.pages.ways.standbyScreen.items =
                kioskProject.template.pages.ways.standbyScreen.items;
        }

        if (hasStandbyStateScreen) {
            obj.template.content.standbyStateScreen.items =
                kioskProject.template.content.standbyStateScreen.items;
        }

        return obj;
    }

    async function updateFilesBasedOnDifferences(
        differences: any[],
        obj: any,
        kioskProject: any
    ) {
        const fileIndexesToUpdate: any = [];

        differences.forEach((diff: any) => {
            if (
                Array.isArray(diff.path) &&
                diff.path[0] === "files" &&
                typeof diff.path[1] === "number"
            ) {
                fileIndexesToUpdate.push(diff.path[1]);
            }
        });

        fileIndexesToUpdate.forEach((index: any) => {
            obj.files[index] = kioskProject.files[index];
        });

        return obj;
    }

    async function handleUpdateApplicationButtonOnClickEvent(
        element: any,
        appId: any
    ) {
        let data: any = {};

        let apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/application/kiosk/template`;
        let { shopProject, warn }: any = await getData(element, appId, true);

        if (shopProject !== null) {
            data[projectId] = {
                ...shopProject,
                modified_at: `${moment().format("MM/DD/YY [at] HH:mm a")}`,
            };
            let dataShop = {
                shopId: element,
                userId: userID,
                franchiseId: franchiseID,
                appId: "KIOSK",
                appId_children: appId,
                operatorId: operatorID,
                uuidUser: uuidUser,
                data: data,
                isConfirmation: true,
            };

            try {
                const data = await fetchGlobal<any>(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(dataShop),
                    method: "POST",
                });
                mutate(apiUrl, data);
                if (data.error) {
                    throw Error("Error while saving selection!");
                }
                return warn;
            } catch (e) {
                toast.error(`${t("There's an error")}!`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
    }
    async function handleUpdateProjectShopsButtonOnClickEvent(
        element: any,
        isError?: boolean
    ) {
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/project/template`;
        let { shopProject, warn }: any = await getData(element);

        if (shopProject !== null) {
            let dataShop = {
                userId: userID,
                franchiseId: franchiseID,
                project_id: "PROJECT",
                projectId: projectId,
                shopId: element,
                operatorId: operatorID,
                uuidUser: uuidUser,
                data: {
                    template: shopProject.template,
                    files: shopProject.files,
                },
                isConfirmation: true,
            };

            try {
                const data = await fetchGlobal<any>(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(dataShop),
                    method: "POST",
                });
                mutate(apiUrl, data);
                if (data.error) {
                    throw Error("Error while saving selection!");
                }

                return warn;
            } catch (e) {
                if (isError !== undefined) isError = true;
                toast.error(`${t("There's an error")}!`, {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
    }

    async function OverwriteTemplateOneShop(isConfirmation: boolean) {
        let warning: any = false;
        warning = await handleUpdateDataOnClickEventV2(
            true,
            shopID,
            isConfirmation
        );
        if (warning === true) {
            toast.warning(
                `${t(
                    "Please note, the modification could not be carried out successfully for one or more stores. Please check the Language, Sales method, and/or payment method configuration."
                )}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        }

        toast.success(`${t("Template saved successfully")}`, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: <CheckboxIcon height={25} width={25} fill="white" />,
        });
        setIsLoading(false);
        setKey(Math.floor(Math.random() * 10000));
        if (projectRowData.Status === "Publié")
            setTimeout(() => {
                RedisPublish();
            }, 3000);
    }
    async function RedisPublish() {
        let apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/application/overwite/redis`;
        let project = JSON.parse(JSON.stringify(projectRowData));
        let shopId;
        if (project.id_boutique !== undefined && project.id_boutique !== "0") {
            shopId = project.id_boutique;
        } else {
            shopId = project.shopId;
        }
        if (project.subRows.length > 0) {
            try {
                const data = await fetchGlobal<any>(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        shopId: shopId,
                    }),
                    method: "POST",
                });
                mutate(apiUrl, data);
                if (data.error) {
                    throw Error("Error while saving selection!");
                }
            } catch (e) {
                console.log(`${t("There's an error")}!`);
            }
        }
    }
    async function OverwriteTemplate(isConfirmation: boolean) {
        setIsModalLoadingOpened(true);
        setModalLoadingData({
            title: "Modification in progress",
            message:
                "Please wait, the modification of the template is in progress",
        });
        setIsModalConfirmationConfigurationKiosk(
            !isModalConfirmationConfigurationKiosk
        );

        await updateTemplateV2(isConfirmation);
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async function updateTemplate() {
        let warning: any = false;
        let isError = false;
        if (projectRowData.shopId !== 0 && projectRowData.shopId !== "0") {
            for (let element of projectRowData.subRows) {
                let warn = await handleUpdateApplicationButtonOnClickEvent(
                    oneShop ? shopID : element.shopId,
                    element.appId
                );
                if (warn === true) {
                    warning = true;
                }
            }
        } else {
            for (let element of JSON.parse(JSON.stringify(projectRowData))
                ?.subRows) {
                if (element.subRows.length !== 0) {
                    for (let el of element?.subRows) {
                        let warn = await handleUpdateApplicationButtonOnClickEvent(
                            oneShop ? shopID : element.shopId,
                            el.appId
                        );
                        if (warn === true) {
                            warning = true;
                        }
                    }
                    let warnShop = await handleUpdateProjectShopsButtonOnClickEvent(
                        element.id_boutique
                    );
                    if (warnShop === true) {
                        warning = true;
                    }
                } else {
                    warning = await handleUpdateProjectShopsButtonOnClickEvent(
                        element.id_boutique,
                        isError
                    );
                }
            }
        }

        if (warning === true) {
            toast.warning(
                `${t(
                    "Please note, the modification could not be carried out successfully for one or more stores. Please check the Language, Sales method, and/or payment method configuration."
                )}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        }

        toast.success(`${t("Template saved successfully")}`, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: <CheckboxIcon height={25} width={25} fill="white" />,
        });
        setIsLoading(false);
        setKey(Math.floor(Math.random() * 10000));
        if (projectRowData.Status === "Publié")
            setTimeout(() => {
                RedisPublish();
            }, 3000);
    }

    async function updateTemplateV2(isConfirmation: boolean) {
        let warning: any = false;

        let project = JSON.parse(JSON.stringify(projectRowData));
        if (project.shopId !== undefined && project.shopId !== 0) {
            warning = await handleUpdateDataOnClickEventV2(
                true,
                project.shopId,
                isConfirmation
            );
        } else {
            warning = await handleUpdateDataOnClickEventV2(
                false,
                "",
                isConfirmation
            );
        }

        if (warning === true) {
            toast.warning(
                `${t(
                    "Please note, the modification could not be carried out successfully for one or more stores. Please check the Language, Sales method, and/or payment method configuration."
                )}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    autoClose: 2000,
                    theme: "colored",
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
        }

        toast.success(`${t("Template saved successfully")}`, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
            theme: "colored",
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            icon: <CheckboxIcon height={25} width={25} fill="white" />,
        });
        setIsLoading(false);
        setKey(Math.floor(Math.random() * 10000));
        if (projectRowData.Status === "Publié")
            setTimeout(() => {
                RedisPublish();
            }, 3000);
    }

    async function handleUpdateDataOnClickEventV2(
        isApplication: boolean,
        id_boutique: string,
        isConfirmation: boolean
    ) {
        let warning: any = false;
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/project/templateV2`;

        let dataShop = {
            franchiseId: franchiseID,
            projectId: projectId,
            itemsDisable: itemsDisable,
            difference: difference,
            isApplication: isApplication,
            id_boutique: id_boutique,
            designation: projectRowData[t("Designation")],
            uuidUser,
            operatorId: operatorID,
            isConfirmation: isConfirmation,
            fidelity: fideltyType,
        };

        try {
            const data = await fetchGlobal<any>(apiUrl, {
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(dataShop),
                method: "POST",
            });
            mutate(apiUrl, data);
            if (data.error) {
                throw Error("Error while saving selection!");
            }
            warning = data.warning;
            setIsModalLoadingOpened(false);

            return warning;
        } catch (e) {
            setIsModalLoadingOpened(false);
            toast.error(`${t("There's an error")}!`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }
    return (
        <React.Fragment>
            <Modal
                isOpen={isModalConfirmationConfigurationKiosk}
                style={{
                    maxWidth: "650px",
                    maxHeight: "500px",
                    marginTop: "300px",
                }}
            >
                <ModalHeader
                    toggle={() => {
                        setIsModalConfirmationConfigurationKiosk(false);
                    }}
                >
                    <StyledH2>{capitalize(t("Alert"))}</StyledH2>
                </ModalHeader>
                <ModalBody>
                    <StyledLabel
                        className="pl-3 m-0"
                        style={{
                            font: "normal normal normal 20px/27px Segoe UI",
                            whiteSpace: "pre-line",
                            opacity: "1",
                            textAlign: "left",
                            color: "#000000",
                        }}
                    >
                        {(projectRowData.shopId === 0 ||
                            projectRowData.shopId === "0") &&
                        !oneShop
                            ? t(
                                  "Attention, if you click on validate, the specific configuration of the shops will be edited."
                              )
                            : t(
                                  "Be careful, if you click on validate, the specific configuration of the applications will be edited."
                              )}
                        !
                    </StyledLabel>
                </ModalBody>
                <ModalFooter className="border-top-0">
                    <CustomMainColorButton
                        outline
                        variant="primary"
                        onClick={() => {
                            if (oneShop) {
                                setIsModalConfirmationConfigurationKiosk(false);
                                OverwriteTemplateOneShop(false);
                            } else {
                                OverwriteTemplate(false);
                            }
                            if (projectRowData.Status === "Publié")
                                setTimeout(() => {
                                    RedisPublish();
                                }, 3000);
                        }}
                        rounded
                    >
                        {t("Cancel")}
                    </CustomMainColorButton>
                    <CustomMainColorButton
                        rounded
                        className="mr-2"
                        variant="primary"
                        onClick={() => {
                            if (oneShop) {
                                setIsModalConfirmationConfigurationKiosk(false);
                                OverwriteTemplateOneShop(true);
                            } else {
                                OverwriteTemplate(true);
                            }
                        }}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
}
